import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Table, Button, Input, DatePicker, Select, Tag, Modal, Popconfirm, message, Empty,
  Tooltip, Progress, Tabs
} from 'antd';
import {
  ReloadOutlined, FileExcelOutlined, DeleteOutlined, EditOutlined, SearchOutlined,
  BarChartOutlined, UnorderedListOutlined, ScissorOutlined, AppstoreOutlined,
  BorderOutlined, CheckCircleOutlined, ClockCircleOutlined, ExportOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { cuttingPlannerApi } from './cuttingPlannerApi';
import {
  hasPermission, PERM, formatThickness, thicknessColor, thicknessChip, groupByThickness, categoryColor, stockDisplayName
} from './cuttingPlannerHelpers';
import { buildPieceColumns, PIECE_GRID_SCROLL_X } from './cuttingPlannerColumns';
import StockIssueModal from './StockIssueModal';
import PlanOffcutModal from './PlanOffcutModal';

const OFFCUT_CREATE_PERM = 'erp.inventory.offcuts.create';
const OFFCUT_PRINT_PERM = 'erp.inventory.offcuts.print';

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
const n3 = (v) => (v == null ? '0' : Number(v).toFixed(3));
const pctOf = (done, all) => (all > 0 ? Math.round((done / all) * 100) : 0);

// Colour-coded grouped headers keyed to the APP THEME tokens
// (primary #1877F2 / success #16A34A / warning #D97706) with faint,
// theme-derived body tints.
const HDR = { actual: '#1877F2', completed: '#16A34A', pending: '#D97706' };
const TINT = { completed: 'rgba(22,163,74,0.06)', pending: 'rgba(245,158,11,0.10)' };
const hdr = (bg) => ({ onHeaderCell: () => ({ style: { background: bg, color: '#fff' } }) });
const tint = (bg) => ({ onCell: () => ({ style: { background: bg } }) });

const THICKNESS_OPTIONS = [4, 5, 6, 8, 10, 12, 15, 19].map((t) => ({ label: `${t} mm`, value: t }));
const PAGE_SIZES = ['20', '50', '100', '200'];

// Dashboard summary card — theme accents (brand / success / warning / slate).
const ACCENT = {
  brand: 'bg-brand-50 text-brand-600',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
  slate: 'bg-surface-100 text-ink-500'
};
function StatBox({ accent = 'slate', icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-surface-200 bg-white shadow-card p-3 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-[16px] shrink-0 ${ACCENT[accent]}`}>{icon}</div>
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-[11px] uppercase tracking-wide text-ink-400">{label}</span>
        <span className="text-[20px] font-bold text-ink-900 tabular-nums leading-tight">{value}</span>
        {sub ? <span className="text-[11px] text-ink-500 tabular-nums truncate">{sub}</span> : null}
      </div>
    </div>
  );
}

export default function PlanTrackerTab({ me, refreshKey, onEdit }) {
  const canExport = hasPermission(me, PERM.EXPORT);
  const canDelete = hasPermission(me, PERM.DELETE);
  const canManage = hasPermission(me, PERM.MANAGE);
  const canIssueStock = hasPermission(me, PERM.ISSUE_STOCK);
  const canRecordOffcut = hasPermission(me, OFFCUT_CREATE_PERM);
  const canPrintOffcut = hasPermission(me, OFFCUT_PRINT_PERM);
  // The saved plan whose "Issue stock" panel is open (null = closed).
  const [issuePlan, setIssuePlan] = useState(null);
  // The saved plan whose "Record off-cuts" panel is open (null = closed).
  const [offcutPlan, setOffcutPlan] = useState(null);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState([]);
  const [categorySummary, setCategorySummary] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [range, setRange] = useState(null);
  const [thickness, setThickness] = useState(null);
  const [optInput, setOptInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [committed, setCommitted] = useState({ opt: '', search: '' });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // The KPI summary is the same for every page of one filter set, so it is
  // fetched only when the filter signature changes (or a caller forces it) —
  // paging never re-runs that full-set aggregate.
  const sumSigRef = useRef(null);

  const load = useCallback(async (force) => {
    setLoading(true);
    try {
      const df = (range && range[0]) ? range[0].format('YYYY-MM-DD') : '';
      const dt = (range && range[1]) ? range[1].format('YYYY-MM-DD') : '';
      const sig = JSON.stringify([df, dt, thickness, committed.opt, committed.search]);
      const wantSummary = force === true || sig !== sumSigRef.current;
      const filters = { page, pageSize, withSummary: wantSummary };
      if (df) filters.dateFrom = df;
      if (dt) filters.dateTo = dt;
      if (thickness != null) filters.thickness = thickness;
      if (committed.opt.trim()) filters.optNo = committed.opt.trim();
      if (committed.search.trim()) filters.search = committed.search.trim();
      const res = (await cuttingPlannerApi.trackerRows(filters)) || {};
      setRows(res.rows || []);
      setTotal(Number(res.total) || 0);
      if (wantSummary) {
        setSummary(Array.isArray(res.summary) ? res.summary : []);
        setCategorySummary(Array.isArray(res.categorySummary) ? res.categorySummary : []);
        sumSigRef.current = sig;
      }
    } catch (err) { message.error(backendError(err, 'Could not load cutting plans.')); }
    finally { setLoading(false); }
  }, [page, pageSize, range, thickness, committed]);

  useEffect(() => { load(); }, [load]);
  // An external refresh (a plan saved elsewhere) also refreshes the KPI summary.
  useEffect(() => {
    if (!refreshKey) return;
    sumSigRef.current = null;
    load();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const applySearch = () => { setCommitted({ opt: optInput.trim(), search: searchInput.trim() }); setPage(1); };
  const clearAll = () => {
    setOptInput(''); setSearchInput(''); setCommitted({ opt: '', search: '' });
    setRange(null); setThickness(null); setPage(1);
  };
  const onRangeChange = (v) => { setRange(v); setPage(1); };
  const onThicknessChange = (v) => { setThickness(v ?? null); setPage(1); };

  const openDetail = useCallback(async (row) => {
    setDetailOpen(true); setDetail({ plan: { planNo: row.planNo, optNo: row.optNo } }); setDetailLoading(true);
    try { setDetail(await cuttingPlannerApi.planItems(row.id)); }
    catch (err) { message.error(backendError(err, 'Could not load plan pieces.')); }
    finally { setDetailLoading(false); }
  }, []);

  const exportRow = useCallback(async (row) => {
    try {
      const data = await cuttingPlannerApi.planItems(row.id);
      await cuttingPlannerApi.exportXls({
        items: data.items, opt_no: row.optNo, plan_date: row.planDate, thickness: row.thickness
      });
      message.success(`Exported OPT-${row.optNo}.`);
    } catch (err) { message.error(backendError(err, 'Could not export the plan.')); }
  }, []);

  const removeRow = useCallback(async (row) => {
    try {
      await cuttingPlannerApi.remove(row.id);
      message.success(`Plan ${row.planNo} deleted; pieces released.`);
      // A delete changes the totals, so refresh the KPI summary as well.
      sumSigRef.current = null;
      if (rows.length === 1 && page > 1) setPage((p) => p - 1); else load(true);
    } catch (err) { message.error(backendError(err, 'Could not delete the plan.')); }
  }, [load, rows.length, page]);

  const kpi = useMemo(() => (summary.length ? summary : groupByThickness(rows)), [summary, rows]);
  // Category grouping is piece-level (a plan can mix categories), so it comes
  // only from the server aggregate — there is no per-plan-row fallback.
  const catKpi = useMemo(() => (Array.isArray(categorySummary) ? categorySummary : []), [categorySummary]);
  const totals = useMemo(() => {
    const acc = { actualPcs: 0, completedPcs: 0, pendingPcs: 0, actualSqmt: 0, completedSqmt: 0, pendingSqmt: 0 };
    for (const g of kpi) {
      acc.actualPcs += g.actualPcs || 0; acc.completedPcs += g.completedPcs || 0; acc.pendingPcs += g.pendingPcs || 0;
      acc.actualSqmt += g.actualSqmt || 0; acc.completedSqmt += g.completedSqmt || 0; acc.pendingSqmt += g.pendingSqmt || 0;
    }
    return acc;
  }, [kpi]);

  const columns = [
    { title: 'OPT No', dataIndex: 'optNo', key: 'opt', width: 88, fixed: 'left',
      render: (v) => <span className="font-semibold text-brand-600">{v}</span> },
    { title: 'Date', dataIndex: 'planDate', key: 'd', width: 96, render: (v) => v ? dayjs(v).format('DD MMM YY') : '—' },
    { title: 'Thk', dataIndex: 'thickness', key: 't', width: 56, align: 'center',
      render: (v) => {
        if (v == null) return '—';
        const c = thicknessChip(v);
        return <span className="inline-block rounded px-1.5 leading-[18px] text-[11px] font-semibold tabular-nums"
          style={{ background: c.bg, color: c.fg }}>{formatThickness(v)}</span>;
      } },
    { title: 'Completion', key: 'prog', width: 138,
      render: (_v, r) => {
        const p = pctOf(r.completedPcs, r.actualPcs);
        return (
          <div className="flex items-center gap-2">
            <Progress percent={p} size="small" showInfo={false} strokeColor={p === 100 ? '#16A34A' : '#1877F2'} className="!m-0 flex-1" />
            <span className="tabular-nums text-[11px] text-slate-500 whitespace-nowrap">{p}%</span>
          </div>
        );
      } },
    { title: 'Stock', dataIndex: 'stockNames', key: 'stk', width: 220, ellipsis: true,
      // string_agg(DISTINCT) dedupes the RAW names, but stockDisplayName strips
      // each to a base label, so distinct sheet sizes can collapse to the same
      // display — dedupe again AFTER stripping so it never reads "CLEAR, CLEAR".
      render: (v) => {
        if (!v) return '—';
        const names = [...new Set(String(v).split(',').map((s) => stockDisplayName(s.trim())).filter(Boolean))];
        return names.length ? names.join(', ') : '—';
      } },
    { title: 'Actual', ...hdr(HDR.actual), children: [
      { title: 'PCS', dataIndex: 'actualPcs', key: 'apc', width: 62, align: 'right', ...hdr(HDR.actual), render: (v) => <span className="tabular-nums font-medium">{v}</span> },
      { title: 'SQMT', dataIndex: 'actualSqmt', key: 'asq', width: 82, align: 'right', ...hdr(HDR.actual), render: (v) => <span className="tabular-nums">{n3(v)}</span> }
    ] },
    { title: 'Completed', ...hdr(HDR.completed), children: [
      { title: 'PCS', dataIndex: 'completedPcs', key: 'cpc', width: 62, align: 'right', ...hdr(HDR.completed), ...tint(TINT.completed), render: (v) => <span className="tabular-nums text-green-600">{v || '—'}</span> },
      { title: 'SQMT', dataIndex: 'completedSqmt', key: 'csq', width: 82, align: 'right', ...hdr(HDR.completed), ...tint(TINT.completed), render: (v) => <span className="tabular-nums text-green-600">{v ? n3(v) : '—'}</span> }
    ] },
    { title: 'Pending', ...hdr(HDR.pending), children: [
      { title: 'PCS', dataIndex: 'pendingPcs', key: 'ppc', width: 62, align: 'right', ...hdr(HDR.pending), ...tint(TINT.pending), render: (v) => <span className="tabular-nums text-amber-600 font-medium">{v || '—'}</span> },
      { title: 'SQMT', dataIndex: 'pendingSqmt', key: 'psq', width: 82, align: 'right', ...hdr(HDR.pending), ...tint(TINT.pending), render: (v) => <span className="tabular-nums text-amber-600">{v ? n3(v) : '—'}</span> }
    ] },
    { title: 'Actions', key: 'x', width: 176, fixed: 'right', align: 'center', render: (_v, r) => (
      <div className="flex items-center justify-center gap-0.5">
        {canManage ? <Tooltip title="Edit"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit && onEdit(r.id)} /></Tooltip> : null}
        {canIssueStock ? <Tooltip title="Issue stock to production"><Button size="small" type="text" icon={<ExportOutlined />} className="!text-brand-600" onClick={() => setIssuePlan({ id: r.id, planNo: r.planNo || `OPT-${r.optNo}` })} /></Tooltip> : null}
        {(canRecordOffcut || canPrintOffcut) ? <Tooltip title="Record / print off-cuts"><Button size="small" type="text" icon={<ScissorOutlined />} className="!text-violet-600" onClick={() => setOffcutPlan({ id: r.id, planNo: r.planNo || `OPT-${r.optNo}` })} /></Tooltip> : null}
        {canExport ? <Tooltip title="Export XLS"><Button size="small" type="text" icon={<FileExcelOutlined />} className="!text-green-600" onClick={() => exportRow(r)} /></Tooltip> : null}
        {canDelete ? (
          <Popconfirm title="Delete plan?" description="Pieces are released back to available." okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeRow(r)}>
            <Tooltip title="Delete"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        ) : null}
      </div>
    ) }
  ];

  const itemColumns = [
    ...buildPieceColumns(),
    { title: 'Status', dataIndex: 'cutDone', key: 'st', width: 96, align: 'center', fixed: 'right',
      // A scrapped piece is dropped from the plan's counts (its remake is tracked
      // on the WO board), so it reads "Scrapped" rather than a stuck "Pending".
      render: (v, r) => r.scrapped
        ? <Tag className="!m-0">Scrapped</Tag>
        : v ? <Tag color="green" className="!m-0">Done</Tag> : <Tag color="orange" className="!m-0">Pending</Tag> }
  ];

  const hasFilters = (range && (range[0] || range[1])) || thickness != null || committed.opt || committed.search;

  const planListTab = (
    <>
      <Table
        className="cutting-grid" size="small" rowKey="id" loading={loading} columns={columns} dataSource={rows}
        scroll={{ x: 1120, y: 540 }} bordered sticky
        pagination={{
          current: page, pageSize, total, size: 'small',
          showSizeChanger: true, pageSizeOptions: PAGE_SIZES,
          showTotal: (t, [a, b]) => `${a}-${b} of ${t}`
        }}
        onChange={(pag) => {
          if (pag.pageSize !== pageSize) { setPageSize(pag.pageSize); setPage(1); }
          else setPage(pag.current);
        }}
        onRow={(r) => ({ onClick: (e) => { if (!e.target.closest('button')) openDetail(r); } })}
        rowClassName={() => 'cursor-pointer'}
        locale={{ emptyText: <Empty description={hasFilters ? 'No plans match these filters.' : 'No cutting plans yet.'} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      />
    </>
  );

  // Area-first KPI: SQMT is the headline metric; pieces are shown only for
  // reference. Completion % is by area to match.
  const overallPct = pctOf(totals.completedSqmt, totals.actualSqmt);
  const kpiTab = (
    <div className="rounded-lg bg-surface-50 border border-surface-200 p-3 flex flex-col gap-3">
      {/* Summary stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatBox accent="brand" icon={<AppstoreOutlined />} label="Total Plans" value={total}
          sub={`${kpi.length} thickness${kpi.length === 1 ? '' : 'es'}`} />
        <StatBox accent="slate" icon={<BorderOutlined />} label="Actual SQMT" value={n3(totals.actualSqmt)}
          sub={`${totals.actualPcs} pcs`} />
        <StatBox accent="green" icon={<CheckCircleOutlined />} label="Completed SQMT" value={n3(totals.completedSqmt)}
          sub={`${totals.completedPcs} pcs · ${overallPct}%`} />
        <StatBox accent="amber" icon={<ClockCircleOutlined />} label="Pending SQMT" value={n3(totals.pendingSqmt)}
          sub={`${totals.pendingPcs} pcs`} />
      </div>

      {/* Overall ring + per-thickness progress chart */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-3">
        <div className="rounded-xl border border-surface-200 bg-white shadow-card p-4 flex flex-col items-center justify-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-ink-400">Overall completion · by area</span>
          <Progress type="dashboard" percent={overallPct} strokeColor="#16A34A" size={148} gapDegree={60}
            format={(p) => <span className="text-ink-900 font-bold text-[22px]">{p}%</span>} />
          <div className="flex items-center gap-4 text-[12px]">
            <span className="text-green-600">Done <b className="tabular-nums">{n3(totals.completedSqmt)}</b></span>
            <span className="text-amber-600">Pending <b className="tabular-nums">{n3(totals.pendingSqmt)}</b></span>
          </div>
        </div>

        <div className="rounded-xl border border-surface-200 bg-white shadow-card p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[13px] font-semibold text-ink-800">Progress by thickness</span>
            <div className="flex items-center gap-3 text-[11px] text-ink-400">
              <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-green-500" /> Completed</span>
              <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" /> Pending</span>
            </div>
          </div>
          {kpi.length ? (
            <div className="flex flex-col gap-2.5">
              {kpi.map((g) => {
                const actual = g.actualSqmt || 0;   // SQMT-first: bars proportioned by area
                const done = g.completedSqmt || 0;
                const p = pctOf(done, actual);
                const donePct = actual ? (done / actual) * 100 : 0;
                const pendPct = actual ? ((actual - done) / actual) * 100 : 0;
                return (
                  <div key={String(g.thickness)} className="flex items-center gap-3">
                    <div className="w-14 shrink-0">
                      <Tag color={typeof g.thickness === 'number' ? thicknessColor(g.thickness) : 'default'} className="!m-0">
                        {typeof g.thickness === 'number' ? formatThickness(g.thickness) : g.thickness} mm
                      </Tag>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="h-5 rounded-md bg-surface-100 flex overflow-hidden">
                        <div className="bg-green-500 h-full flex items-center justify-end pr-1 transition-all" style={{ width: `${donePct}%` }}>
                          {donePct > 18 ? <span className="text-[10px] text-white font-semibold tabular-nums">{n3(done)}</span> : null}
                        </div>
                        <div className="bg-amber-400 h-full flex items-center pl-1 transition-all" style={{ width: `${pendPct}%` }}>
                          {pendPct > 18 ? <span className="text-[10px] text-white font-semibold tabular-nums">{n3(actual - done)}</span> : null}
                        </div>
                      </div>
                    </div>
                    <div className="w-24 shrink-0 text-right text-[11px] text-ink-500">
                      <span className="tabular-nums">{g.plans} plan{g.plans === 1 ? '' : 's'}</span>
                      <span className="mx-1 text-surface-200">·</span>
                      <span className="tabular-nums text-ink-800 font-semibold">{p}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty className="!my-4" description="No data for the current filters." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </div>

      {/* Progress by glass category (piece-level; a plan may mix categories) */}
      <div className="rounded-xl border border-surface-200 bg-white shadow-card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] font-semibold text-ink-800">Progress by category</span>
          <div className="flex items-center gap-3 text-[11px] text-ink-400">
            <span>{catKpi.length} categor{catKpi.length === 1 ? 'y' : 'ies'}</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-green-500" /> Completed</span>
            <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" /> Pending</span>
          </div>
        </div>
        {catKpi.length ? (
          <div className="flex flex-col gap-2.5">
            {catKpi.map((g) => {
              const actual = g.actualSqmt || 0;   // SQMT-first: bars proportioned by area
              const done = g.completedSqmt || 0;
              const p = pctOf(done, actual);
              const donePct = actual ? (done / actual) * 100 : 0;
              const pendPct = actual ? ((actual - done) / actual) * 100 : 0;
              return (
                <div key={String(g.category)} className="flex items-center gap-3">
                  <div className="w-28 shrink-0">
                    <Tag color={categoryColor(g.category)} className="!m-0 !max-w-full truncate">{g.category}</Tag>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-5 rounded-md bg-surface-100 flex overflow-hidden">
                      <div className="bg-green-500 h-full flex items-center justify-end pr-1 transition-all" style={{ width: `${donePct}%` }}>
                        {donePct > 18 ? <span className="text-[10px] text-white font-semibold tabular-nums">{n3(done)}</span> : null}
                      </div>
                      <div className="bg-amber-400 h-full flex items-center pl-1 transition-all" style={{ width: `${pendPct}%` }}>
                        {pendPct > 18 ? <span className="text-[10px] text-white font-semibold tabular-nums">{n3(actual - done)}</span> : null}
                      </div>
                    </div>
                  </div>
                  <div className="w-24 shrink-0 text-right text-[11px] text-ink-500">
                    <span className="tabular-nums">{g.plans} plan{g.plans === 1 ? '' : 's'}</span>
                    <span className="mx-1 text-surface-200">·</span>
                    <span className="tabular-nums text-ink-800 font-semibold">{p}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <Empty className="!my-4" description="No category data for the current filters." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Command toolbar — light, theme-native */}
      <div className="rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
            <ScissorOutlined className="text-brand-600 text-[15px]" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-ink-800">Cutting Plan Tracker</span>
            <span className="text-[11px] text-ink-400">Completion from live cutting scans</span>
          </div>
        </div>
        <div className="h-7 w-px bg-surface-200 mx-1 hidden lg:block" />
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide text-ink-400">OPT</span>
          <Input size="small" placeholder="e.g. 1013" value={optInput} className="!w-24"
            onChange={(e) => setOptInput(e.target.value)} onPressEnter={applySearch} />
        </div>
        <DatePicker.RangePicker size="small" value={range} onChange={onRangeChange} format="DD MMM YY" />
        <Select size="small" allowClear placeholder="Thickness" className="!w-32"
          value={thickness} onChange={onThicknessChange} options={THICKNESS_OPTIONS} />
        <Input size="small" allowClear prefix={<SearchOutlined className="text-ink-400" />} className="!w-56"
          placeholder="WO / customer / PI / item" value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); if (!e.target.value && committed.search) setCommitted((c) => ({ ...c, search: '' })); }}
          onPressEnter={applySearch} />
        <Button size="small" type="primary" icon={<SearchOutlined />} onClick={applySearch}>Search</Button>
        <Tooltip title="Reload"><Button size="small" icon={<ReloadOutlined />} onClick={() => load(true)} /></Tooltip>
        {hasFilters ? <Button size="small" type="link" onClick={clearAll}>Clear</Button> : null}
        <div className="flex-1" />
        <div className="flex items-baseline gap-1.5 rounded-lg bg-white border border-surface-200 px-2.5 py-1">
          <span className="text-[16px] font-bold text-ink-900 tabular-nums leading-none">{total}</span>
          <span className="text-[11px] text-ink-400 uppercase tracking-wide">plan{total === 1 ? '' : 's'}</span>
        </div>
      </div>

      <Tabs
        size="small"
        items={[
          { key: 'list', label: <span className="flex items-center gap-1.5"><UnorderedListOutlined /> Plan List</span>, children: planListTab },
          { key: 'kpi', label: <span className="flex items-center gap-1.5"><BarChartOutlined /> Progress KPI</span>, children: kpiTab }
        ]}
      />

      {/* Detail modal */}
      <Modal
        open={detailOpen} onCancel={() => setDetailOpen(false)} width="94vw" style={{ maxWidth: 1240 }} footer={null}
        title={detail ? <span className="font-semibold">Plan {detail.plan?.planNo || `OPT-${detail.plan?.optNo}`}</span> : 'Plan'}
      >
        <Table
          className="cutting-grid" size="small" rowKey="id" loading={detailLoading} columns={itemColumns}
          dataSource={detail?.items || []} scroll={{ x: PIECE_GRID_SCROLL_X, y: 420 }}
          pagination={(detail?.items || []).length > 50
            ? { pageSize: 50, size: 'small', showSizeChanger: false, showTotal: (t) => `${t} pieces` }
            : false}
          locale={{ emptyText: <Empty description="No pieces on this plan." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
        {/* Mark-as-Cut is intentionally not offered — cut completion is recorded
            at the Scan Station and derived from those scans. */}
        <div className="text-[12px] text-slate-400 mt-2">
          Status is derived from real cutting scans made at the Scan Station.
        </div>
      </Modal>

      <StockIssueModal
        open={!!issuePlan}
        planId={issuePlan && issuePlan.id}
        planNo={issuePlan && issuePlan.planNo}
        canIssue={canIssueStock}
        onClose={() => setIssuePlan(null)}
        onChanged={() => load(true)}
      />

      <PlanOffcutModal
        open={!!offcutPlan}
        planId={offcutPlan && offcutPlan.id}
        planNo={offcutPlan && offcutPlan.planNo}
        canRecord={canRecordOffcut}
        canPrint={canPrintOffcut}
        onClose={() => setOffcutPlan(null)}
        onChanged={() => load(true)}
      />
    </div>
  );
}
